<script setup>
import ImovelAPI from 'dashboard/api/imoveis';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  value: { type: Object, required: true },
});

const emit = defineEmits(['update:value']);
const { t } = useI18n();

const update = (field, value) =>
  emit('update:value', { ...props.value, [field]: value });
const normalizeCep = value =>
  value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
const normalizeValue = (field, value) =>
  field === 'numero_escritura' ? value.replace(/\D/g, '') : value;

const lookupCep = async value => {
  const cep = normalizeCep(value);
  update('cep_escritura', cep);
  if (cep.length !== 9) return;

  const response = await ImovelAPI.normalizeAddress({ cep });
  const address = response?.data || {};
  if (!address.address && !address.city && !address.state) return;

  emit('update:value', {
    ...props.value,
    cep_escritura: cep,
    endereco_escritura: address.address || props.value.endereco_escritura,
    cidade_escritura: address.city || props.value.cidade_escritura,
    bairro_escritura: address.neighborhood || props.value.bairro_escritura,
    estado_escritura: address.state || props.value.estado_escritura,
  });
};
</script>

<template>
  <div>
    <div v-if="value.local_escritura" class="grid gap-4 sm:grid-cols-2">
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.ZIP') }}
        <input
          :value="value.cep_escritura"
          inputmode="numeric"
          maxlength="9"
          :placeholder="t('POST_SALE.FORM.DEED.ZIP_PLACEHOLDER')"
          class="min-h-10 rounded-lais border border-lais-border px-2 font-normal"
          @input="lookupCep($event.target.value)"
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.ADDRESS') }}
        <input
          :value="value.endereco_escritura"
          :placeholder="t('POST_SALE.FORM.DEED.ADDRESS_PLACEHOLDER')"
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-2 font-normal"
          readonly
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.NUMBER') }}
        <input
          :value="value.numero_escritura"
          inputmode="numeric"
          class="min-h-10 rounded-lais border border-lais-border px-2 font-normal"
          @input="
            update(
              'numero_escritura',
              normalizeValue('numero_escritura', $event.target.value)
            )
          "
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.UNIT') }}
        <input
          :value="value.unidade_escritura"
          :placeholder="t('POST_SALE.FORM.DEED.UNIT_PLACEHOLDER')"
          class="min-h-10 rounded-lais border border-lais-border px-2 font-normal"
          @input="update('unidade_escritura', $event.target.value)"
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.COMPLEMENT') }}
        <input
          :value="value.complemento_escritura"
          :placeholder="t('POST_SALE.FORM.DEED.COMPLEMENT_PLACEHOLDER')"
          class="min-h-10 rounded-lais border border-lais-border px-2 font-normal"
          @input="update('complemento_escritura', $event.target.value)"
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.CITY') }}
        <input
          :value="value.cidade_escritura"
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-2 font-normal"
          readonly
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.STATE') }}
        <input
          :value="value.estado_escritura"
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-2 font-normal"
          readonly
        />
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DEED.NEIGHBORHOOD') }}
        <input
          :value="value.bairro_escritura"
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-2 font-normal"
          readonly
        />
      </label>
    </div>
  </div>
</template>
